import {
  Button,
  Form,
  notification,
  Pagination,
  Row,
  Table,
  Tag,
  Typography,
  Tree,
} from 'antd';
import React, { useContext, useState } from 'react';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@tanstack/react-query';
import TableAction from '../common/TableAction';
import { newObjectKeys } from '../../utils/utility';
import RenderModalForm from '../common/renderModalForm';
import { RoleContext } from '../../hooks/context/RoleContext';
import roleService from '../../services/roleManagementRequest';

const RoleTable = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createNew, setCreateNew] = useState(false);
  const [form] = Form.useForm();
  const {
    permissionQuery: { data: rawPermission },
  } = useContext(RoleContext);
  const dataPermission = Array.isArray(rawPermission) ? rawPermission : rawPermission?.data || rawPermission?.rows || [];
  const [permissions, setPermissions] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Query riêng có truyền page/limit
  const rolesQuery = useQuery({
    queryKey: ['list-roles', page, limit],
    queryFn: () => roleService.getList({ page, limit }),
  });

  // Extract data + total từ response
  const rawData = rolesQuery?.data;
  const roleList = Array.isArray(rawData) ? rawData : rawData?.roles || rawData?.data || rawData?.rows || [];
  const total = rawData?.total || rawData?.totalItems || roleList.length || 0;

  const { mutate: submitForm, isPending: isLoading } = useMutation({
    mutationFn: createNew
      ? (data) => roleService.create(data)
      : (data) => roleService.update(data.id, data),
    onSuccess: () => {
      notification.success({
        message: `${createNew ? 'Tạo mới' : 'Chỉnh sửa'} nhóm quyền thành công`,
      });
      rolesQuery.refetch();
      setIsModalOpen(false);
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const fields = [
    { name: 'id', colClass: 'hidden' },
    { name: 'name', lg: 24, label: 'Tên nhóm quyền', rules: [{ required: true }] },
    { name: 'description', lg: 24, label: 'Mô tả' },
    {
      name: 'status', lg: 24, label: 'Trạng thái', type: 'select',
      rules: [{ required: true }],
      options: [
        { value: 1, label: 'Kích hoạt' },
        { value: 0, label: 'Chờ kích hoạt' },
      ],
    },
    {
      type: 'html',
      content: (
        <>
          <Typography.Text className="mb-4 block text-lg">
            Vui lòng chọn tính năng
          </Typography.Text>
          <Tree
            checkable
            checkStrictly={true}
            defaultExpandedKeys={dataPermission?.map((item) => item.id)}
            checkedKeys={permissions}
            treeData={dataPermission
              ?.filter((item) => item?.status === 1)
              ?.map((item) => ({
                title: item.name,
                key: item.id,
                children: item.actions.map((i) => ({
                  title: i.name,
                  key: i.id,
                })),
              }))}
            onCheck={(checkedKeys, e) => {
              let keysChecked = checkedKeys.checked;
              if (e.checked === false && e.node.children) {
                const idChildren = e.node.children.map((item) => item.key);
                keysChecked = keysChecked.filter((item) => !idChildren.includes(item));
              } else if (e.checked === true && e.node.children) {
                const idChildren = e.node.children.map((item) => item.key);
                keysChecked = [...new Set([...keysChecked, ...idChildren])];
              } else if (e.checked === true && !e.node.children) {
                const findParent = dataPermission.find((item) =>
                  item.actions.some((i) => i.id === e.node.key)
                );
                if (!keysChecked.includes(findParent.id)) {
                  keysChecked = [...keysChecked, findParent.id];
                }
              } else if (e.checked === false && !e.node.children) {
                keysChecked = keysChecked.filter((item) => item !== e.node.key);
                const findParent = dataPermission.find((item) =>
                  item.actions.some((i) => i.id === e.node.key)
                );
                if (keysChecked.includes(findParent.id)) {
                  const findChildren = dataPermission.find((item) => item.id === findParent.id)?.actions;
                  const findChildrenSet = new Set(findChildren?.map((item) => item.id));
                  const commonElements = keysChecked.filter((item) => findChildrenSet.has(item));
                  if (!commonElements.length) {
                    keysChecked = keysChecked.filter((item) => item !== findParent.id);
                  }
                }
              }
              setPermissions(keysChecked);
            }}
          />
        </>
      ),
    },
  ];

  const handleSubmitForm = (data) => {
    submitForm({
      ...data,
      permissions: permissions.filter((i) =>
        dataPermission.some((o) => o.actions.some((l) => l.id === i) || o.id === i)
      ),
    });
  };

  const handlePaginationChange = (newPage, newLimit) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  return (
    <>
      <RenderModalForm
        loadingSubmit={isLoading}
        modalTitle={createNew ? 'Tạo mới nhóm quyền' : 'Sửa nhóm quyền'}
        isOpenModal={isModalOpen}
        handleCloseModal={() => setIsModalOpen(false)}
        modalWidth={800}
        onFinish={handleSubmitForm}
        fields={fields}
        form={form}
      />
      <Row justify="space-between" align="bottom" className="my-2 cursor-pointer">
        <Typography.Title level={5} className="mb-0">#ROLE</Typography.Title>
        <Button
          className="bg-white border-violet-500 text-violet-500"
          onClick={() => {
            form.resetFields();
            let keys = [];
            for (let item of dataPermission || []) {
              keys = keys.concat([item.id, ...item.actions.map((i) => i.id)]);
            }
            setPermissions(keys);
            setCreateNew(true);
            setIsModalOpen(true);
          }}
        >
          Thêm mới nhóm quyền
        </Button>
      </Row>

      <Table
        bordered
        className="min-w-full"
        loading={rolesQuery.isLoading || rolesQuery.isFetching}
        pagination={false}
        scroll={{ x: 1000 }}
        dataSource={roleList.map((item, key) => ({ ...item, key }))}
        columns={[
          {
            title: 'STT',
            width: 60,
            align: 'center',
            render: (_, __, index) => (page - 1) * limit + index + 1,
          },
          { title: 'Tên nhóm quyền', dataIndex: 'name' },
          { title: 'Mô tả', dataIndex: 'description', render: (value) => value || '-' },
          { title: 'Số thành viên', dataIndex: 'total_user' },
          {
            title: 'Trạng thái', dataIndex: 'status',
            render: (value) => (
              <Tag color={value ? 'cyan' : 'red'}>{value ? 'Kích hoạt' : 'Chờ kích hoạt'}</Tag>
            ),
          },
          {
            title: 'Ngày tạo',
            dataIndex: 'created_at',
            render: (value) => value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-',
          },
          {
            title: '',
            render: (item) => (
              <TableAction
                record={item}
                onEdit={(record) => {
                  form.resetFields();
                  form.setFieldsValue(newObjectKeys(record, ['name', 'description', 'status', 'id']));
                  setPermissions([...record.permission_ids]);
                  setCreateNew(false);
                  setIsModalOpen(true);
                }}
                onDelete={async (record) => {
                  try {
                    await roleService.delete(record.id);
                    rolesQuery.refetch();
                  } catch (error) {
                    console.error('Xóa nhóm quyền thất bại:', error);
                  }
                }}
              />
            ),
          },
        ]}
      />

      {/* Pagination */}
      {total > 0 && (
        <div className="py-3 flex justify-between items-center">
          <span className="text-xs text-gray-500">
            Hiển thị <strong>{(page - 1) * limit + 1}</strong> - <strong>{Math.min(page * limit, total)}</strong> trên tổng <strong>{total}</strong>
          </span>
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            onChange={handlePaginationChange}
            showSizeChanger
            pageSizeOptions={['10', '20', '50']}
            locale={{ items_per_page: '/ trang' }}
          />
        </div>
      )}
    </>
  );
};

export default RoleTable;
